// Shared helpers for showing WHICH taxes make up a line/order's tax total —
// e.g. "VAT 14%: 12.60" and "Service Charge: 3.00" as separate rows instead
// of one lump number, so a service charge is never confused with VAT. Used
// by both the live Checkout cart and the historical Order receipt, which is
// why this lives here rather than duplicated in each page. See
// docs/order-design.md section 2 and internal/services/tax_calc.go.

// aggregateTaxBreakdown sums each line's per-tax breakdown into one summary
// list keyed by tax id (falling back to name for a line that has no id,
// which shouldn't normally happen). `lines` is an array of breakdown arrays
// — one per cart/order line — each entry shaped like
// { tax_id, tax_name, rate, amount, is_price_inclusive }.
export function aggregateTaxBreakdown(lines) {
  const totals = new Map();
  for (const line of lines || []) {
    for (const t of line || []) {
      const key = t.tax_id ?? t.tax_name;
      const existing = totals.get(key);
      if (existing) {
        existing.amount += t.amount;
      } else {
        totals.set(key, { ...t });
      }
    }
  }
  return Array.from(totals.values()).sort((a, b) => (a.tax_name || "").localeCompare(b.tax_name || ""));
}

// taxRateLabel renders a breakdown line's rate the way it's actually
// configured: a percentage tax shows "14%", but a fixed_amount tax's Rate is
// a flat amount per unit (in the base currency, before the sale's exchange
// rate is applied) so it should show the currency symbol instead — showing
// "%" next to a flat amount is misleading. currencyLabel is the sale's
// effective currency symbol/code (e.g. order.currency?.symbol).
export function taxRateLabel(t, currencyLabel) {
  if (!t || t.rate === undefined || t.rate === null) return "";
  if (t.calculation_method === "fixed_amount") {
    return `${t.rate}${currencyLabel ? " " + currencyLabel : ""}`;
  }
  return `${t.rate}%`;
}

// parseItemBreakdown safely reads an OrderItem's stored tax_breakdown_json
// (a JSON string snapshotted at sale time — see OrderItem.TaxBreakdownJSON)
// back into the same shape the live Checkout calculation produces. Returns
// [] for an old/empty row rather than throwing.
export function parseItemBreakdown(item) {
  if (!item?.tax_breakdown_json) return [];
  try {
    const parsed = JSON.parse(item.tax_breakdown_json);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}
